import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ArrowRight, Shield, Users, Heart, Eye, Lock, HelpCircle } from 'lucide-react';

/**
 * FAMILIES LANDING PAGE
 * 
 * Entry point for parents discovering LikeKind's family features.
 * 
 * Structure:
 * 1. What it is
 * 2. Who it's for
 * 3. How it works (3 bullets)
 * 4. Safety promise
 * 5. Primary CTA
 * 6. Secondary CTA to FAQ
 */
export default function FamiliesLanding() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-sm" style={{ backgroundColor: 'rgba(244, 239, 230, 0.95)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <button onClick={() => navigate('/')} className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_kindplatform/artifacts/my73uiau_file_00000000f09c720c9463ab506718d330.png" 
                alt="LikeKind" 
                className="w-8 h-8 rounded-lg"
              />
              <span className="font-bold" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>LikeKind</span>
            </button>
            <Button 
              onClick={() => navigate('/auth?role=parent')}
              size="sm"
              className="rounded-full"
              style={{ backgroundColor: 'var(--lk-primary-green)', color: 'var(--lk-warm-sand)' }}
              data-testid="families-enter-btn"
            >
              Enter
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-12">
        
        {/* What It Is */}
        <section className="mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold mb-4" style={{ fontFamily: 'Outfit', color: 'var(--fy-text-primary)' }}>
            LikeKind for Families
          </h1>
          <p className="text-lg leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
            A calm, private place schools use to coordinate schedules and communication — without social media.
          </p>
        </section>

        {/* What This Is */}
        <section className="mb-12 p-6 rounded-2xl" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
          <p className="text-sm leading-relaxed" style={{ color: 'var(--fy-text-secondary)' }}>
            LikeKind is used by schools and programs to share information with families in a way that is structured, limited, and intentional.
          </p>
          <ul className="mt-4 space-y-2 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--lk-secondary-orange)' }} />
              It is not a social network
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--lk-secondary-orange)' }} />
              It is not designed to keep children scrolling
            </li>
            <li className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--lk-secondary-orange)' }} />
              It does not track behavior or sell data
            </li>
          </ul>
        </section>

        {/* What You'll See */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold mb-6" style={{ color: 'var(--fy-text-primary)' }}>What you&apos;ll see as a parent</h2>
          <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            Depending on how your child&apos;s school uses LikeKind, you may see:
          </p>
          <div className="space-y-4">
            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--lk-terra-muted)' }}>
                <Users className="w-4 h-4" style={{ color: 'var(--lk-secondary-orange)' }} />
              </div>
              <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                School calendars and closures
              </p>
            </div>
            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--lk-terra-muted)' }}>
                <Heart className="w-4 h-4" style={{ color: 'var(--lk-secondary-orange)' }} />
              </div>
              <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                Announcements and reminders
              </p>
            </div>
            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--lk-terra-muted)' }}>
                <Lock className="w-4 h-4" style={{ color: 'var(--lk-secondary-orange)' }} />
              </div>
              <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                Notes, documents, or permission slips
              </p>
            </div>
            <div className="flex items-start gap-4 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--lk-terra-muted)' }}>
                <Eye className="w-4 h-4" style={{ color: 'var(--lk-secondary-orange)' }} />
              </div>
              <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                Teacher-to-student communication (visible to parents where applicable)
              </p>
            </div>
          </div>
          <p className="text-sm mt-4" style={{ color: 'var(--fy-text-disabled)' }}>
            Your view is read-only unless the school enables parent interaction.
          </p>
        </section>

        {/* What Your Child Can and Cannot Do */}
        <section className="mb-12">
          <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>What your child can and cannot do</h2>
          <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
            LikeKind is built with clear boundaries. Children and students:
          </p>
          <ul className="space-y-2 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            <li className="flex items-center gap-2">
              <Lock className="w-4 h-4" style={{ color: 'var(--lk-primary-green)' }} />
              Are not searchable
            </li>
            <li className="flex items-center gap-2">
              <Lock className="w-4 h-4" style={{ color: 'var(--lk-primary-green)' }} />
              Do not have public profiles
            </li>
            <li className="flex items-center gap-2">
              <Lock className="w-4 h-4" style={{ color: 'var(--lk-primary-green)' }} />
              Cannot message other students
            </li>
            <li className="flex items-center gap-2">
              <Lock className="w-4 h-4" style={{ color: 'var(--lk-primary-green)' }} />
              Cannot be contacted by businesses or sponsors
            </li>
          </ul>
          <p className="text-sm mt-4" style={{ color: 'var(--fy-text-disabled)' }}>
            Communication is role-based and limited to what the school allows.
          </p>
        </section>

        {/* Safety Is Built In */}
        <section className="mb-12 p-6 rounded-2xl" style={{ backgroundColor: 'var(--lk-sage-muted)', border: '1px solid var(--lk-primary-green)' }}>
          <div className="flex items-center gap-3 mb-3">
            <Shield className="w-5 h-5" style={{ color: 'var(--lk-primary-green)' }} />
            <h2 className="font-semibold" style={{ color: 'var(--lk-primary-green)' }}>Safety is built in</h2>
          </div>
          <ul className="text-sm space-y-2" style={{ color: 'var(--fy-text-primary)' }}>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>No ads or targeted content</span>
            </li>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>No algorithms ranking posts or people</span>
            </li>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>No engagement metrics (likes, streaks, scores)</span>
            </li>
            <li className="flex items-start gap-2">
              <Lock className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: 'var(--lk-primary-green)' }} />
              <span>Automated policy enforcement for public spaces</span>
            </li>
          </ul>
          <p className="text-sm font-medium mt-4" style={{ color: 'var(--lk-primary-green)' }}>
            Safety features are not optional upgrades. They are part of the system.
          </p>
        </section>

        {/* Privacy and Data */}
        <section className="mb-12 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
          <h3 className="font-semibold mb-2" style={{ color: 'var(--fy-text-primary)' }}>Privacy and data</h3>
          <p className="text-sm mb-2" style={{ color: 'var(--fy-text-secondary)' }}>
            LikeKind does not:
          </p>
          <ul className="text-sm space-y-1 ml-4" style={{ color: 'var(--fy-text-secondary)' }}>
            <li>• Sell or share personal data</li>
            <li>• Track behavior for advertising</li>
            <li>• Build marketing profiles</li>
          </ul>
          <p className="text-sm mt-3" style={{ color: 'var(--fy-text-disabled)' }}>
            Information is used only to operate the service.
          </p>
        </section>

        {/* Your Role */}
        <section className="mb-12 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-muted)', border: '1px solid var(--fy-border-subtle)' }}>
          <h3 className="font-semibold mb-2" style={{ color: 'var(--fy-text-primary)' }}>Your role</h3>
          <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            You remain the parent. LikeKind does not replace family communication or decision-making. 
            It exists only to make coordination clearer and safer.
          </p>
        </section>

        {/* Questions */}
        <section className="mb-12 p-4 rounded-xl" style={{ backgroundColor: 'var(--fy-surface-card)', border: '1px solid var(--fy-border-subtle)' }}>
          <h3 className="font-semibold mb-2" style={{ color: 'var(--fy-text-primary)' }}>Questions?</h3>
          <p className="text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            Each school sets its own policies within LikeKind&apos;s rules. If you have questions about how your school is using the platform, contact them directly.
          </p>
          <p className="text-sm mt-2" style={{ color: 'var(--fy-text-secondary)' }}>
            General questions about LikeKind can be found in the <button onClick={() => navigate('/faq/families')} className="underline">FAQ</button>.
          </p>
        </section>

        {/* CTAs */}
        <section className="space-y-4">
          <Button 
            onClick={() => navigate('/auth?role=parent')}
            className="w-full h-14 rounded-xl text-lg font-semibold"
            style={{ backgroundColor: 'var(--lk-primary-green)', color: 'var(--lk-warm-sand)' }}
            data-testid="families-create-account-btn"
          >
            Create Parent Account
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          <Button 
            onClick={() => navigate('/faq/families')}
            variant="outline"
            className="w-full h-12 rounded-xl font-medium"
            style={{ borderColor: 'var(--fy-border-subtle)', color: 'var(--fy-text-secondary)' }}
            data-testid="families-faq-btn"
          >
            <HelpCircle className="w-4 h-4 mr-2" />
            Family Safety FAQ
          </Button>
        </section>

        {/* Footer Links */}
        <footer className="mt-16 pt-8 flex flex-wrap gap-4 justify-center text-sm" style={{ borderTop: '1px solid var(--fy-border-subtle)', color: 'var(--fy-text-disabled)' }}>
          <button onClick={() => navigate('/for/individuals')} className="hover:underline">For Individuals</button>
          <span>·</span>
          <button onClick={() => navigate('/for/schools')} className="hover:underline">For Schools</button>
          <span>·</span>
          <button onClick={() => navigate('/for/institutions')} className="hover:underline">For Institutions</button>
          <span>·</span>
          <button onClick={() => navigate('/privacy')} className="hover:underline">Privacy</button>
          <span>·</span>
          <button onClick={() => navigate('/terms')} className="hover:underline">Terms</button>
        </footer>
      </main>
    </div>
  );
}
